import axios from "axios";
import { tMsg } from "../../../ToastNotifications";
import { useState } from "react";
import { API_DOMAIN } from "../../../../api/apiAgent";
import { useAtomValue } from "jotai";
import { hostDataAtom } from "../../../../atoms";

// TODO: Should get these from the backend?
// const bucket = "lightwell-test";
// const region = "ap-southeast-2";
const chunkSize = 1024 * 1024 * 5; // TODO: Should get this from config later, needs to be minimum 5MB

type MultiPartRequest = {
  multipart_upload_id: string;
  urls: string[];
};

type UploadPart = {
  ETag: string;
  PartNumber: number;
};

const isVideo = (fileName: string) => {
  // TODO: Load `supportedVideoTypes` from backend?
  const supportedVideoTypes = [".mp4"];
  const fileNameParts = fileName.split(".");
  return supportedVideoTypes.includes(
    `.${fileNameParts[fileNameParts.length - 1]}`,
  );
};

export const getStillVideoFrameFromUrl = (url: string) => {
  return new Promise<string>((resolve, reject) => {
    // Source (modified from here): https://stackoverflow.com/a/36046775
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.src = url;
    video.volume = 0;
    video
      .play()
      .then(() => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        video.pause();
        video.remove();
        resolve(canvas.toDataURL("image/jpg"));
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Given some files, return (presigned) MultiPartRequest(s) for uploading them
 * @param files
 * @returns
 */
export const getPresignedMultiPartUploadFromFiles = (
  files: File[],
  bucket: string,
) => {
  return new Promise<MultiPartRequest[]>((resolve, reject) => {
    const getPresignedPromises: Array<Promise<MultiPartRequest>> = [];

    files.forEach((f) => {
      const fileParts = f.name.split(".");
      const fileExt = f.name.split(".")[fileParts.length - 1];

      // Get presigned URLs and MultiPartUploadId for each file
      getPresignedPromises.push(
        new Promise((resolve, reject) => {
          console.log("getPresignedPromises");
          const resp: MultiPartRequest = {
            multipart_upload_id: "",
            urls: [],
          };
          if (f.name === "") {
            resolve(resp);
          }
          console.log("setting fetchArgs");
          const fetchArgs = {
            method: "PUT",
            body: JSON.stringify({
              bucket: bucket,
              key: f.name,
              size: f.size,
              chunk_size: chunkSize,
            }),
            headers: new Headers({
              // TODO: Make this stricter
              "Content-Type": isVideo(f.name)
                ? "video/" + fileExt
                : "image/" + fileExt,
            }),
          };
          console.log("fetchArgs:", fetchArgs);

          axios
            .put(
              `${API_DOMAIN}/s3/presigned_url/`,
              {
                bucket: bucket,
                key: f.name,
                size: f.size,
                chunk_size: chunkSize,
              },
              {
                withCredentials: true,
                headers: {
                  // TODO: Make this stricter
                  "Content-Type": isVideo(f.name)
                    ? "video/" + fileExt
                    : "image/" + fileExt,
                },
              },
            )
            .then((resp) => {
              console.log("data:", resp.data);
              resolve(resp.data);
            })
            .catch((err) => reject(err));
        }),
      );
    });

    // Resolve promises and return MultiPartRequest(s)
    Promise.all(getPresignedPromises)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Upload the given file
 * @param file
 */
export const uploadFile = (
  file: File,
  onUploadFinished: (fl: string) => void,
  updateProgress: (fl: number) => void,
  bucket: string,
  region: string,
) => {
  const files: File[] = file ? [file] : [];
  getPresignedMultiPartUploadFromFiles(files, bucket)
    .then((uploadRequests) => {
      // Do each uploadRequest
      uploadRequests.forEach((req, index) => {
        console.log("req", req);
        if (req.urls.length === 0) {
          return;
        }

        // Find the corresponding file for this uploadRequest
        const file = files[index];

        // Upload each file part of max size `chunkSize`, use partsLeft to track/update upload progress (in chunks)
        let partsLeft = req.urls.length;
        console.log("pre request");
        const partUploads = req.urls.map((u, i) => {
          const chunkStart = i * chunkSize;
          let chunkEnd = chunkStart + chunkSize;
          if (chunkEnd > file.size) {
            chunkEnd = file.size;
          }

          /* //PUT this chunk of the file on S3
          return fetch(u, {
            method: "PUT",
            body: file.slice(chunkStart, chunkEnd),
            mode: "cors",
          })
            .then((resp) => {
              if (resp.status === 200) {
                // Use `partsLeft` here to track the progress of the file upload
                partsLeft--;
                console.log(
                  `${(1 - partsLeft / partUploads.length) * 100}% Completed`
                );
                return resp;
              }
            })
            .catch((err) => {
              console.warn(err);
              return err;
            });
          */

          return axios
            .put(u, file.slice(chunkStart, chunkEnd))
            .then((resp) => {
              if (resp.status === 200) {
                partsLeft--;
                const remaining = (1 - partsLeft / partUploads.length) * 100;
                console.log(`${remaining}% Completed`);
                updateProgress(remaining);
              }
              return resp;
            })
            .catch((err) => {
              console.warn(err);
              tMsg.error("Error uploading file");
              return err;
            });
        });

        // Resolve all promises then, construct `uploadParts` to tell S3 which "chunks" belong to this file
        Promise.all(partUploads)
          .then((results) => {
            const uploadParts: UploadPart[] = [];
            results.forEach((part, index) => {
              uploadParts.push({
                ETag: part.headers.get("etag"),
                PartNumber: index + 1,
              });
            });
            return uploadParts;
          })
          .then((uploadedParts) => {
            // Make request to the backend to `completeMultipartUpload`
            const body = {
              bucket: bucket,
              key: file.name,
              upload_id: req.multipart_upload_id,
              multipart_upload: uploadedParts,
              expected_filesize: file.size,
            };

            /* Last fetch to complete upload
            fetch("http://localhost:8080/s3/complete_multipart_upload", {
              method: "PUT",
              body: JSON.stringify(body),
              mode: "cors",
            });
            */

            axios.put(`${API_DOMAIN}s3/complete_multipart_upload/`, body, {
              withCredentials: true,
            });

            const location =
              `https://${bucket}.s3.${region}.amazonaws.com/${file.name}`.replaceAll(
                " ",
                "+",
              );
            console.log(`Object location: ${location}`);
            onUploadFinished(location);
          });
      });
    })
    .finally(() => {
      // TODO: This is where we'd submit the body to create/update an item
    });
};
